import React, { useEffect, useRef, useState, useMemo } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { memberApi, getMemberToken } from "../../lib/api";
import { useMemberAuth } from "../../context/MemberAuthContext";
import MemberLayout, { Avatar } from "../../components/MemberLayout";
import { toast } from "../../hooks/use-toast";
import { ArrowLeft, Send, User } from "lucide-react";

/**
 * Escape HTML then linkify http(s) URLs. Returns an object usable in
 * dangerouslySetInnerHTML — we only ever inject text WE built, so
 * scripts / iframes / on* attributes supplied by the sender can never
 * survive the escape step.
 */
const URL_RE = /(https?:\/\/[^\s<>]+[^\s<>.,;:!?)\]}"'`])/gi;
function escapeHtml(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
function linkify(text) {
  const safe = escapeHtml(text);
  return safe.replace(URL_RE, (url) => {
    // url is already HTML-escaped; put it into href verbatim (safe since we
    // escaped quotes) and label it plainly.
    return `<a href="${url}" target="_blank" rel="noopener noreferrer nofollow" class="msg-link">${url}</a>`;
  });
}

export const MessagesList = () => {
  const { refreshUnread } = useMemberAuth();
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    memberApi.get('/member/messages/conversations').then(({ data }) => setConvs(data)).catch(() => {}).finally(() => setLoading(false));
    refreshUnread();
  }, [refreshUnread]);
  return (
    <MemberLayout>
      <div className="section-eyebrow text-[#0d2a20]/60 mb-2">Members · Messages</div>
      <h1 className="hero-title text-[40px] md:text-[56px] leading-none mb-6">Your <span className="font-serif-i text-[#e0684d]">conversations</span></h1>
      <div className="rounded-lg overflow-hidden" style={{ background: '#fbf8ef' }}>
        {loading ? (
          <div className="p-10 text-center text-[14px] text-[#0d2a20]/60">Loading…</div>
        ) : convs.length === 0 ? (
          <div className="p-10 text-center">
            <User size={32} className="mx-auto text-[#0d2a20]/40 mb-3" />
            <div className="text-[15px] text-[#0d2a20]/80">No messages yet.</div>
            <div className="text-[13px] text-[#0d2a20]/60 mt-1">Head to the <Link to="/member/directory" className="link-underline text-[#e0684d]">Directory</Link> to start a conversation.</div>
          </div>
        ) : (
          convs.map(c => (
            <Link key={c.id} to={`/member/messages/${c.id}`} className="flex items-center gap-4 p-4 border-b last:border-b-0 hover:bg-[#f6f0dc]/70 transition-colors" style={{ borderColor: 'rgba(13,42,32,0.06)' }}>
              <Avatar src={c.other?.avatar_url} name={c.other?.display_name} size={44} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-[14px] truncate">{c.other?.display_name || 'Unknown'}</div>
                  <div className="text-[11px] text-[#0d2a20]/50 whitespace-nowrap">{c.last_message_at ? new Date(c.last_message_at).toLocaleDateString() : ''}</div>
                </div>
                <div className="text-[13px] text-[#0d2a20]/70 truncate">{c.last_message_preview || 'No messages yet'}</div>
              </div>
              {c.unread > 0 && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: '#e0684d', color: 'white' }}>{c.unread}</span>}
            </Link>
          ))
        )}
      </div>
    </MemberLayout>
  );
};

export const MessageThread = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const { member, loading: authLoading, refreshUnread } = useMemberAuth();
  const [conv, setConv] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  // ---- Auth gate: if we don't have a token, redirect to login preserving destination ----
  useEffect(() => {
    if (authLoading) return;
    if (!getMemberToken() || !member) {
      const next = encodeURIComponent(`/member/messages/${id}`);
      nav(`/login?next=${next}`, { replace: true });
    }
  }, [authLoading, member, id, nav]);

  const load = async () => {
    try {
      const { data } = await memberApi.get(`/member/messages/conversations/${id}`);
      setConv(data);
      setLoadError(null);
      refreshUnread();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        // Token was invalid/expired mid-session — bounce through login
        const next = encodeURIComponent(`/member/messages/${id}`);
        nav(`/login?next=${next}`, { replace: true });
        return;
      }
      if (status === 403) {
        setLoadError("You don't have permission to view this conversation.");
        return;
      }
      // 404 or anything else genuinely means "not found"
      setLoadError("Conversation not found.");
    }
  };

  useEffect(() => {
    if (member) load();
    // eslint-disable-next-line
  }, [id, member]);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [conv]);

  // Auto-grow the composer up to a sensible max
  const autoGrow = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const max = 220; // px
    el.style.height = Math.min(el.scrollHeight, max) + 'px';
  };
  useEffect(autoGrow, [text]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await memberApi.post(`/member/messages/conversations/${id}/messages`, { content: text });
      setConv(c => ({ ...c, messages: [...(c?.messages || []), data.message] }));
      setText('');
      // Reset composer height
      requestAnimationFrame(() => { if (textareaRef.current) textareaRef.current.style.height = 'auto'; });
    } catch (err) {
      toast({ title: 'Could not send', description: err?.response?.data?.detail || 'Try again.' });
    } finally { setSending(false); }
  };

  if (authLoading || (!member && !loadError)) {
    return <MemberLayout><div className="rounded-lg p-8" style={{ background: '#fbf8ef' }}>Signing you in…</div></MemberLayout>;
  }

  if (loadError) {
    return (
      <MemberLayout>
        <Link to="/member/messages" className="inline-flex items-center gap-1 text-[13px] link-underline mb-4"><ArrowLeft size={14} /> All conversations</Link>
        <div className="rounded-lg p-8 text-center" style={{ background: '#fbf8ef' }} data-testid="thread-not-found">
          <div className="text-[16px] font-medium mb-2">{loadError}</div>
          <div className="text-[13px] text-[#0d2a20]/70">Head back to <Link to="/member/messages" className="link-underline text-[#e0684d]">your conversations</Link>.</div>
        </div>
      </MemberLayout>
    );
  }

  if (!conv) return <MemberLayout><div className="rounded-lg p-8" style={{ background: '#fbf8ef' }}>Loading…</div></MemberLayout>;

  return (
    <MemberLayout>
      <Link to="/member/messages" className="inline-flex items-center gap-1 text-[13px] link-underline mb-4"><ArrowLeft size={14} /> All conversations</Link>
      <div className="rounded-lg overflow-hidden flex flex-col" style={{ background: '#fbf8ef', height: 'calc(100vh - 200px)' }}>
        <div className="flex items-center gap-3 p-4 border-b" style={{ borderColor: 'rgba(13,42,32,0.08)' }}>
          <Avatar src={conv.other?.avatar_url} name={conv.other?.display_name} size={40} />
          <div className="min-w-0">
            <div className="font-medium text-[14px] truncate">{conv.other?.display_name}</div>
            <div className="text-[12px] text-[#0d2a20]/60 truncate">{conv.other?.headline}</div>
          </div>
          {conv.other && <Link to={`/member/directory/${conv.other.id}`} className="ml-auto text-[12px] link-underline text-[#e0684d]">View profile</Link>}
        </div>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="thread-scroll">
          {conv.messages.map(m => {
            const mine = m.sender_id === member.id;
            return (
              <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] rounded-lg px-3 py-2 text-[14px]`} style={mine ? { background: '#0f3d2e', color: '#f4efe4' } : { background: '#ece5d3', color: '#0d2a20' }}>
                  <div
                    className={`whitespace-pre-wrap break-words msg-body ${mine ? 'msg-body-mine' : ''}`}
                    data-testid={`msg-${m.id}`}
                    dangerouslySetInnerHTML={{ __html: linkify(m.content) }}
                  />
                  <div className={`text-[10px] mt-1 ${mine ? 'text-[#f4efe4]/60' : 'text-[#0d2a20]/50'}`}>{new Date(m.created_at).toLocaleString()}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="p-3 border-t flex gap-2 items-end" style={{ borderColor: 'rgba(13,42,32,0.08)' }}>
          <textarea
            ref={textareaRef}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey && !e.altKey && !e.metaKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Write a message… (Shift + Enter for a new line)"
            rows={1}
            data-testid="msg-composer"
            className="flex-1 px-3 py-2 rounded-md bg-white border text-[14px] resize-none leading-relaxed"
            style={{ borderColor: 'rgba(13,42,32,0.15)', minHeight: 42, maxHeight: 220 }}
          />
          <button onClick={send} disabled={sending || !text.trim()} data-testid="msg-send-btn" className="inline-flex items-center gap-1 px-4 py-2 rounded-md btn-coral text-[13px] disabled:opacity-60 shrink-0"><Send size={13} /> Send</button>
        </div>
      </div>
    </MemberLayout>
  );
};
